import { jsx } from 'react/jsx-runtime';
import { styled } from '@flex-design-system/stitches-react';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import classNames from 'classnames';
import { forwardRef, useRef } from 'react';
import usePopperPresence from '../../../hooks/usePopperPresence.mjs';
import { usePopperZIndexStyle } from '../../../hooks/usePopperZIndexStyle.mjs';
import { usePreventTransitionInteraction } from '../../../hooks/usePreventTransitionInteraction.mjs';
import { ShakingAnimation } from '../../layout/ShakingAnimation.mjs';
import { usePopoverContext, PopoverProvider } from '../context.mjs';
import { DEFAULT_COLLISION_PADDING } from '../shared/constants.mjs';
import { content, contentInner, contentMinWidth } from './clickPopover.vanilla.mjs';

const ClickPopover = (props) => {
  const {
    open: openProp,
    defaultOpen,
    onOpenChange,
    modal = true,
    onClose,
    ...rootProps
  } = props;
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  const { ref: presenceRef } = usePopperPresence({
    open,
    onPresenceChange: (isPresent) => !isPresent && onClose?.()
  });
  return /* @__PURE__ */ jsx(PopoverProvider, { open, presenceRef, children: /* @__PURE__ */ jsx(
    PopoverPrimitive.Root,
    {
      modal,
      open,
      onOpenChange: setOpen,
      ...rootProps
    }
  ) });
};
const StyledContent = styled(PopoverPrimitive.Content);
const StyledDiv = styled("div");
const ClickPopoverContent = forwardRef(
  ({
    sideOffset = 4,
    css,
    children,
    className,
    style: styleFromProps,
    prefersReducedMotion,
    zIndex,
    closeOnInteractOutside = true,
    collisionPadding = DEFAULT_COLLISION_PADDING,
    onInteractOutside,
    ...props
  }, forwardedRef) => {
    const { open, presenceRef } = usePopoverContext("ClickPopover.Content");
    const composedRefs = useComposedRefs(presenceRef, forwardedRef);
    const animationRef = useRef(null);
    const zIndexStyle = usePopperZIndexStyle(open, zIndex);
    const preventTransitionInteraction = usePreventTransitionInteraction({
      open
    });
    return /* @__PURE__ */ jsx(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx(
      StyledContent,
      {
        sideOffset,
        ref: composedRefs,
        collisionPadding,
        className: content({ prefersReducedMotion }),
        style: {
          ...zIndexStyle,
          ...styleFromProps,
          ...preventTransitionInteraction.style
        },
        ...props,
        onKeyDownCapture: (e) => {
          props.onKeyDownCapture?.(e);
          preventTransitionInteraction.onKeyDownCapture?.(e);
        },
        onInteractOutside: composeEventHandlers(onInteractOutside, (event) => {
          if (!closeOnInteractOutside) {
            animationRef.current?.triggerAnimation();
            event.preventDefault();
          }
        }),
        children: /* @__PURE__ */ jsx(ShakingAnimation, { ref: animationRef, children: /* @__PURE__ */ jsx(
          StyledDiv,
          {
            css,
            className: classNames(contentInner, contentMinWidth, className),
            children
          }
        ) })
      }
    ) });
  }
);
ClickPopover.Trigger = PopoverPrimitive.Trigger;
ClickPopover.Close = PopoverPrimitive.Close;
ClickPopover.Content = ClickPopoverContent;
ClickPopover.Anchor = PopoverPrimitive.Anchor;

export { ClickPopover };
//# sourceMappingURL=ClickPopover.mjs.map
